// These helpers are calling this template's own server-side routes
// so, they are not directly calling Marketplace API or Integration API.
// You can find these api endpoints from 'server/api/...' directory

import appSettings from '../config/settings';
import { types as sdkTypes, transit } from './sdkLoader';
import Decimal from 'decimal.js';

export const apiBaseUrl = marketplaceRootURL => {
  const port = process.env.REACT_APP_DEV_API_SERVER_PORT;
  const useDevApiServer = process.env.NODE_ENV === 'development' && !!port;

  // In development, the dev API server is running in a different port
  if (useDevApiServer) {
    return `http://localhost:${port}`;
  }

  // SERVER-SIDE (SSR): call our own Express process directly over localhost.
  // We must NOT route SSR self-calls through the public hostname: Cloudflare
  // sits in front of www.bigloads.com and challenges datacenter-to-datacenter
  // requests, returning an HTML challenge page instead of JSON (this is what
  // broke the Workshop category/type pages on prod, 2026-10-12). Keeping this
  // separate from REACT_APP_MARKETPLACE_ROOT_URL also lets that env var stay
  // on the public domain, which is what canonical/og:url/JSON-LD URLs use.
  if (typeof window === 'undefined') {
    // ⚠️ sharetribe-scripts (CRA fork) INLINES `process.env` as a frozen object
    // literal at BUILD time in BOTH bundles — so process.env.PORT /
    // process.env.SSR_INTERNAL_API_URL are ALWAYS undefined here at runtime.
    // server/index.js (plain Node, not webpack-processed) therefore passes the
    // runtime port via a global before the SSR bundle handles requests.
    // DefinePlugin only rewrites the literal expression `process.env`, so a
    // global read survives the build. (Found the hard way, 2026-10-13.)
    const g = typeof global !== 'undefined' ? global : undefined;
    const runtimeBase = g && g.__SSR_INTERNAL_API_BASE__;
    if (runtimeBase) {
      // e.g. http://127.0.0.1:<PORT> — the server's own Express process.
      // 127.0.0.1 (not 'localhost') avoids Node 18+ fetch/undici IPv6 (::1)
      // resolution surprises; Express app.listen(PORT) binds all interfaces.
      return String(runtimeBase).replace(/\/$/, '');
    }
    // Last-resort fallback (old behavior; these are build-time-inlined values).
    const envRootURL = process.env.REACT_APP_MARKETPLACE_ROOT_URL || process.env.BASE_URL || '';
    return envRootURL.replace(/\/$/, '');
  }

  // CLIENT-SIDE: use the given marketplaceRootURL parameter or the same
  // domain and port as the frontend.
  if (marketplaceRootURL) {
    return marketplaceRootURL.replace(/\/$/, '');
  }

  return window.location.origin;
};

// Application type handlers for JS SDK.
//
// NOTE: keep in sync with `typeHandlers` in `server/api-util/sdk.js`
export const typeHandlers = [
  // Use Decimal type instead of SDK's BigDecimal.
  {
    type: sdkTypes.BigDecimal,
    customType: Decimal,
    writer: v => new sdkTypes.BigDecimal(v.toString()),
    reader: v => new Decimal(v.value),
  },
];

const serialize = data => {
  return transit.write(data, { typeHandlers, verbose: appSettings.sdk.transitVerbose });
};

const deserialize = str => {
  return transit.read(str, { typeHandlers });
};

const methods = {
  POST: 'POST',
  GET: 'GET',
  PUT: 'PUT',
  PATCH: 'PATCH',
  DELETE: 'DELETE',
};

// If server/api returns data from SDK, you should set Content-Type to 'application/transit+json'
const request = (path, options = {}) => {
  const url = `${apiBaseUrl()}${path}`;
  const { credentials, headers, body, ...rest } = options;

  // If headers are not set, we assume that the body should be serialized as transit format.
  const shouldSerializeBody =
    (!headers || headers['Content-Type'] === 'application/transit+json') && body;
  const bodyMaybe = shouldSerializeBody ? { body: serialize(body) } : {};

  const fetchOptions = {
    credentials: credentials || 'include',
    // Since server/api mostly talks to Marketplace API using SDK,
    // we default to 'application/transit+json' as content type (as SDK uses transit).
    headers: headers || { 'Content-Type': 'application/transit+json' },
    ...bodyMaybe,
    ...rest,
  };

  // Use the global fetch. In the browser this is window.fetch; during SSR
  // (Node) `window` is undefined but a global `fetch` exists. Referencing
  // `window.fetch` directly threw "window is not defined" on the server.
  const fetchFn = typeof window !== 'undefined' && window.fetch ? window.fetch.bind(window) : fetch;

  return fetchFn(url, fetchOptions).then(res => {
    const contentTypeHeader = res.headers.get('Content-Type');
    const contentType = contentTypeHeader ? contentTypeHeader.split(';')[0] : null;

    if (res.status >= 400) {
      return res.json().then(data => {
        let e = new Error();
        e = Object.assign(e, data);

        throw e;
      });
    }
    if (contentType === 'application/transit+json') {
      return res.text().then(deserialize);
    } else if (contentType === 'application/json') {
      return res.json();
    }
    return res.text();
  });
};

// Keep the previous parameter order for the post method.
// For now, only POST has own specific function, but you can create more or use request directly.
const post = (path, body, options = {}) => {
  const requestOptions = {
    ...options,
    method: methods.POST,
    body,
  };

  return request(path, requestOptions);
};
const get = (path, options = {}) => {
  const requestOptions = {
    ...options,
    method: methods.GET,
  };

  return request(path, requestOptions);
};

// Fetch transaction line items from the local API endpoint.
//
// See `server/api/transaction-line-items.js` to see what data should
// be sent in the body.
export const transactionLineItems = body => {
  return post('/api/transaction-line-items', body);
};

// Initiate a privileged transaction.
//
// With privileged transitions, the transactions need to be created
// from the backend. This endpoint enables sending the order data to
// the local backend, and passing that to the Marketplace API.
//
// See `server/api/initiate-privileged.js` to see what data should be
// sent in the body.
export const initiatePrivileged = body => {
  return post('/api/initiate-privileged', body);
};

export const initiateExtendBookingApi = body => {
  return post('/api/initiate-extend-booking', body);
};

// Transition a transaction with a privileged transition.
//
// This is similar to the `initiatePrivileged` above. It will use the
// backend for the transition. The backend endpoint will add the
// payment line items to the transition params.
//
// See `server/api/transition-privileged.js` to see what data should
// be sent in the body.
export const transitionPrivileged = body => {
  return post('/api/transition-privileged', body);
};

export const transitionMetaDataPrivilegedApi = body => {
  return post('/api/update-metadata-transition', body);
};

// Create user with identity provider (e.g. Facebook or Google)
//
// If loginWithIdp api call fails and user can't authenticate to Marketplace API with idp
// we will show option to create a new user with idp.
// For that user needs to confirm data fetched from the idp.
// After the confirmation, this endpoint is called to create a new user with confirmed data.
//
// See `server/api/auth/createUserWithIdp.js` to see what data should
// be sent in the body.
export const createUserWithIdp = body => {
  return post('/api/auth/create-user-with-idp', body);
};

export const claimListing = body => {
  return post('/api/claim-listing', body);
};

export const fetchBlogs = body => {
  return post('/api/strapi/fetch-blogs', body);
};

export const fetchBlog = body => {
  return post('/api/strapi/fetch-blog', body);
};

export const fetchFAQS = body => {
  return post('/api/strapi/fetch-faqs', body);
};

export const fetchSeoCategory = body => {
  return post('/api/strapi/fetch-seo-category', body);
};

export const fetchTagsFilter = body => {
  return post('/api/strapi/fetch-blogs-tags', body);
};

export const fetchSeoPages = body => {
  return post('/api/strapi/fetch-seo-categories', body);
};

export const fetchStates = body => {
  return post('/api/strapi/fetch-states', body);
};

// ─── The Workshop (trailer-accessories hub) ──────────────────────────────────
export const fetchWorkshopHome = (body = {}) => {
  return post('/api/strapi/fetch-workshop-home', body);
};

export const fetchWorkshopType = body => {
  return post('/api/strapi/fetch-workshop-type', body);
};

export const fetchWorkshopCategory = body => {
  return post('/api/strapi/fetch-workshop-category', body);
};

export const fetchWorkshopProduct = body => {
  return post('/api/strapi/fetch-workshop-product', body);
};

export const sendSendGridNotification = body => {
  return post('/api/sendgrid/send-notification', body);
};

export const deleteFileAWSApi = body => {
  return post('/api/delete-AWS-file', body);
};

export const refundStripeIntent = body => {
  return post('/api/initiate-refund', body);
};
export const updateTransitionMetadata = body => {
  return post('/api/update-metadata', body);
};
export const fetchAvalabilityExceptionApi = body => {
  return post('/api/avalablility-exception', body);
};
export const capturePaymentApi = body => {
  return post('/api/capture-payment', body);
};

export const updateStateCityForListing = body => {
  return post('/api/get-state-city-for-listing', body);
};
export const fetchSubscriptionProductsApi = () => {
  return get('/api/fetch-subscription-products');
};
export const verifyCouponAPI = body => {
  return post('/api/verify-coupon', body);
};

export const createSubscriptionAPI = body => {
  return post('/api/create-subscription', body);
};

export const cancelSubscriptionAPI = body => {
  return post('/api/stripe/cancel-subscription', body);
};

// Opens the Stripe-hosted Customer Portal (manage card / invoices / cancel).
// Customer id + return_url are resolved server-side from the authed user.
export const createBillingPortalSessionAPI = () => {
  return post('/api/stripe/create-billing-portal-session', {});
};

// Haul Star "Pause instead of Cancel". All of these resolve the acting user +
// their subscription SERVER-SIDE from the session — the client only supplies the
// resume date (epoch seconds) and optional retention reason/detail.
// Pause: { resumesAt, reason?, detail? }
export const pauseSubscriptionAPI = body => {
  return post('/api/stripe/pause-subscription', body || {});
};
// Resume now (clears the pause).
export const resumeSubscriptionAPI = () => {
  return post('/api/stripe/resume-subscription', {});
};
// Change the resume date on an existing pause: { resumesAt }
export const updateResumeDateAPI = body => {
  return post('/api/stripe/update-resume-date', body || {});
};
// Member-facing cancel at period end: { reason?, detail? }
export const cancelMembershipAPI = body => {
  return post('/api/stripe/cancel-membership', body || {});
};
// Undo a pending period-end cancel.
export const reactivateMembershipAPI = () => {
  return post('/api/stripe/reactivate-membership', {});
};

// Starts a Stripe-hosted Checkout session for a new subscription. priceId is the
// Stripe recurring price id. Customer + return URLs are resolved server-side.
export const createCheckoutSessionAPI = body => {
  return post('/api/stripe/create-checkout-session', body);
};

export const fetchUserDetailsAPI = body => {
  return post('/api/sharetribe/fetch-user', body);
};

export const trackListingAnalytics = body => {
  return post('/api/track-listing-analytics', body);
};

// "What Trailer Do I Need?" quiz: AI recommendation + lead capture.
// stage='teaser' returns the recommendation + nearby count (no listing links);
// stage='unlock' (email required) returns full results + matching listings.
export const quizRecommend = body => {
  return post('/api/quiz/recommend', body);
};

// export const fetchGoogleReviewsAPI = mapsUrl => {
//   const qs = `?mapsUrl=${encodeURIComponent(mapsUrl)}`;
//   return get(`/api/google-reviews${qs}`, {
//     headers: { 'Content-Type': 'application/json' },
//   });
// };

export const fetchGoogleReviewsAPI = body => {
  return post('/api/google-reviews', body);
};

// Haul Star bulk discounts: push the owner's chosen discount presets to ALL of
// their listings in one call (and save them as the profile default so new
// listings inherit them). `body` = { discounts: string[] of preset keys }.
// Server derives the owner from the session + enforces the Haul Star gate.
// Returns { status, discounts, totalListings, updatedListings }.
export const applyBulkDiscountsAPI = body => {
  return post('/api/apply-bulk-discounts', body);
};

/**
 * fetchOwnerMessageStats — READ-ONLY.
 * Calls the /api/owner/message-stats endpoint and returns
 * { messages: number, recent: Array }.
 * Returns a safe default on any error so the dashboard never breaks.
 */
export const fetchOwnerMessageStats = () => {
  return get('/api/owner/message-stats', {
    headers: { 'Content-Type': 'application/json' },
  }).catch(() => ({ messages: 0, recent: [] }));
};

// Haul Star image protection: upload an image FILE through our server so it can
// be scanned for baked-in contact info (phone/email/URL) and redacted for free
// owners before it lands on Sharetribe. Sends multipart/form-data (NOT transit)
// with the auth cookie. Returns { activeImage, pair, isMember, hasContact, ... }.
// See server/api/imageContactScan.js.
export const scanAndUploadImageAPI = file => {
  const formData = new FormData();
  formData.append('file', file);
  const url = `${apiBaseUrl()}/api/scan-and-upload-image`;
  const fetchFn = typeof window !== 'undefined' && window.fetch ? window.fetch.bind(window) : fetch;
  return fetchFn(url, {
    method: 'POST',
    credentials: 'include',
    // NOTE: do NOT set Content-Type — the browser sets the multipart boundary.
    body: formData,
  }).then(res => {
    if (res.status >= 400) {
      return res.json().then(data => {
        let e = new Error(data.error || 'Image upload failed');
        e = Object.assign(e, data);
        throw e;
      });
    }
    return res.json();
  });
};

// Insurance proof upload (Listing Richness Phase B). Reuses the existing AWS
// upload route (server/api/awsFileUpload.js) that contract files use. Returns
// { objectURL, key }. We persist ONLY the `key` (+ filename) to the owner's
// profile privateData so the doc reference never appears on the public profile
// or PDP — the public badge only needs the boolean attestation. Sends multipart
// with the auth cookie; accepts PDF/JPG/PNG.
export const uploadInsuranceDocAPI = file => {
  const formData = new FormData();
  formData.append('file', file);
  const url = `${apiBaseUrl()}/api/upload-file-aws`;
  const fetchFn = typeof window !== 'undefined' && window.fetch ? window.fetch.bind(window) : fetch;
  return fetchFn(url, {
    method: 'POST',
    credentials: 'include',
    // NOTE: do NOT set Content-Type — the browser sets the multipart boundary.
    body: formData,
  }).then(res => {
    if (res.status >= 400) {
      return res.json().then(data => {
        let e = new Error(data.error || 'Insurance document upload failed');
        e = Object.assign(e, data);
        throw e;
      });
    }
    return res.json();
  });
};
